/**
 * Configuracion del DESPLIEGUE, resuelta en el navegador.
 *
 * <p>Vite sustituye `import.meta.env` literalmente durante `vite build`, asi que todo lo que se lea
 * de ahi queda escrito DENTRO del JavaScript y una imagen publicada apunta para siempre a lo que se
 * le paso al construirla. Para la URL de la API eso no vale: el mismo digest se despliega en dev, en
 * QA y en produccion, y con la URL horneada el checkout de QA llama a la API de produccion. El
 * sintoma es cruel --la pagina carga, los `health` estan en verde, y el comprador ve «No pudimos
 * conectarnos» al intentar pagar-- y ya costo dos incidentes.
 *
 * <p>Por eso la coordenada de despliegue se lee en tiempo de EJECUCION: el contenedor escribe
 * `config.js` al arrancar con lo que diga su entorno, `index.html` lo carga antes del bundle, y aqui
 * se decide. El valor horneado no se retira: sigue siendo el respaldo, de modo que un `dist/` servido
 * por cualquier otra cosa se comporta igual que hasta hoy.
 */

/** Ultimo recurso. Produccion y no una cadena vacia: ver el test que lo fija. */
const API_BASE_URL_POR_DEFECTO = 'https://api-web-prod.apipay.cl';

/** Lo que `config.js` publica en la ventana. Todo opcional: el fichero puede no decir nada. */
export interface RuntimeConfig {
  readonly apiBaseUrl?: string | undefined;
}

/**
 * URL absoluta http(s), o null.
 *
 * <p>Se valida porque decide A DONDE viajan la clave publicable y el `client_secret` de cada sesion.
 * Una relativa apuntaria al propio origen del checkout --que sirve estaticos-- y el comprador veria
 * un error de red imposible de diagnosticar.
 */
function urlUtilizable(valor: unknown): string | null {
  if (typeof valor !== 'string') {
    return null;
  }
  const limpio = valor.trim();
  if (limpio.length === 0) {
    return null;
  }
  try {
    const parsed = new URL(limpio);
    return parsed.protocol === 'https:' || parsed.protocol === 'http:' ? limpio : null;
  } catch {
    return null;
  }
}

/**
 * Decide la base de la API: primero la del AMBIENTE, si no la HORNEADA, y si ninguna vale,
 * produccion.
 *
 * @param runtime lo que haya en `window.__APIPAY_CONFIG__`, tal cual y sin confiar en su forma: el
 *     fichero lo escribe el arranque del contenedor y puede estar vacio, a medias o corrupto
 * @param baked el valor que Vite inlino al construir (`import.meta.env.VITE_API_BASE_URL`)
 */
export function resolveApiBaseUrl(runtime: unknown, baked: string | undefined): string {
  const delAmbiente =
    typeof runtime === 'object' && runtime !== null
      ? urlUtilizable((runtime as { apiBaseUrl?: unknown }).apiBaseUrl)
      : null;

  return delAmbiente ?? urlUtilizable(baked) ?? API_BASE_URL_POR_DEFECTO;
}

/** Lo que publico `config.js`, o `undefined` si no se cargo (dev server, `dist/` suelto, 404). */
export function readRuntimeConfig(win: Window = window): unknown {
  return (win as unknown as { __APIPAY_CONFIG__?: unknown }).__APIPAY_CONFIG__;
}
